import * as React from 'react'
import { useState, useContext, useEffect, useMemo, useCallback } from 'react'
import { QueryClientProvider } from 'react-query'
import { format, parseISO, addDays, subDays } from 'date-fns'
import buildQueryClient from '../helpers/queryClient'
import { useResourcesRecordsIndexQuery } from '../../hooks/queries/resources_records'
import DateNavigation from './DateNavigation'
import TotalRow from './TotalRow'
import RecordsList from './RecordsList'
import RecordsFilter from './RecordsFilter'
import JobnumberSuggestions from './JobnumberSuggestions'
import ErrorMessage from '../job_plannings/list_app/ErrorMessage'
import { export_all_to_nav_resources_records_path } from '../../routes'
import { TimeDeltaProvider, TimeDeltaContext } from './TimeDeltaContext'
import ExportToNavButton from './ExportToNavButton'
import { EditModeProvider } from './EditModeContext'
import { absenceMatchesFilter, adjustmentMatchesFilter, recordMatchesFilter } from './recordMatchesFilter'

const queryClient = buildQueryClient()

type ResourcesRecordsIndexAppProps = {
  initialDate: string
}

function useCurrentDate(initialDate: string) {
  const [currentDate, setCurrentDate] = useState<string>(initialDate)

  const currentDateObj = useMemo(() => parseISO(currentDate), [currentDate])

  const setDate = useCallback((date: Date) => {
    setCurrentDate(format(date, 'yyyy-MM-dd'))
  }, [])

  const goPrevDate = useCallback(() => {
    setCurrentDate((prev) => {
      const date = parseISO(prev)
      return format(subDays(date, 1), 'yyyy-MM-dd')
    })
  }, [])

  const goNextDate = useCallback(() => {
    setCurrentDate((prev) => {
      const date = parseISO(prev)
      return format(addDays(date, 1), 'yyyy-MM-dd')
    })
  }, [])

  useEffect(() => {
    const url = new URL(window.location.href)
    url.searchParams.set('date', currentDate)
    window.history.replaceState({}, '', url.toString())
  }, [currentDate])

  return { currentDate, currentDateObj, setDate, goPrevDate, goNextDate }
}

function ResourcesRecordsIndexContent({ initialDate }: ResourcesRecordsIndexAppProps) {
  const { currentDate, currentDateObj, setDate, goPrevDate, goNextDate } = useCurrentDate(initialDate)
  const { data, isLoading, isError, error } = useResourcesRecordsIndexQuery(currentDate)
  const timeDeltaContext = useContext(TimeDeltaContext)
  const [filterTerm, setFilterTerm] = useState('')
  const optimisticTimeDelta = timeDeltaContext ? timeDeltaContext.getTotalDelta() : 0
  const indexData = data?.data
  const hasAnyRecords = useMemo(() => {
    if (!indexData) return false
    return (
      indexData.records.length > 0 ||
      !!indexData.absence_day ||
      !!indexData.presence_day ||
      indexData.adjustments.length > 0
    )
  }, [indexData])
  const hasEditableRecords = useMemo(() => {
    if (!indexData) return false
    return indexData.records.some(
      (record) => !record.exported && !record.from_approved_week && record.times.length === 1
    )
  }, [indexData])
  const totalEntriesCount = useMemo(() => {
    if (!indexData) return 0
    return (
      indexData.records.length +
      (indexData.absence_day ? 1 : 0) +
      (indexData.presence_day ? 1 : 0) +
      indexData.adjustments.length
    )
  }, [indexData])
  const shouldShowFilter = totalEntriesCount > 3
  const filteredRecords = useMemo(() => {
    if (!indexData || !shouldShowFilter) return indexData?.records || []
    return indexData.records.filter((record) => recordMatchesFilter(record, filterTerm))
  }, [filterTerm, indexData, shouldShowFilter])
  const filteredAbsenceDay = useMemo(() => {
    if (!indexData || !shouldShowFilter) return indexData?.absence_day || null
    return indexData.absence_day && absenceMatchesFilter(indexData.absence_day, filterTerm)
      ? indexData.absence_day
      : null
  }, [filterTerm, indexData, shouldShowFilter])
  const filteredPresenceDay = useMemo(() => {
    if (!indexData || !shouldShowFilter) return indexData?.presence_day || null
    return indexData.presence_day && absenceMatchesFilter(indexData.presence_day, filterTerm)
      ? indexData.presence_day
      : null
  }, [filterTerm, indexData, shouldShowFilter])
  const filteredAdjustments = useMemo(() => {
    if (!indexData || !shouldShowFilter) return indexData?.adjustments || []
    return indexData.adjustments.filter((adjustment) => adjustmentMatchesFilter(adjustment, filterTerm))
  }, [filterTerm, indexData, shouldShowFilter])
  const filteredEntriesCount =
    filteredRecords.length + (filteredAbsenceDay ? 1 : 0) + (filteredPresenceDay ? 1 : 0) + filteredAdjustments.length
  const shouldShowSuggestions = shouldShowFilter && filterTerm.trim().length > 0 && filteredEntriesCount === 0

  useEffect(() => {
    setFilterTerm('')
  }, [currentDate])

  useEffect(() => {
    if (!shouldShowFilter && filterTerm) {
      setFilterTerm('')
    }
  }, [filterTerm, shouldShowFilter])

  if (isLoading) {
    return (
      <div className="text-center">
        <i className="fa fa-spinner fa-spin fa-2x" />
      </div>
    )
  }

  if (isError) {
    return <ErrorMessage error={error} isError={isError} />
  }

  if (!data?.data) {
    return null
  }

  const totalRowProps = {
    total: indexData.total,
    date: currentDate,
    canCreateEntry: indexData.can_create_entry,
    hasPresenceDay: !!indexData.presence_day,
    hasAbsenceDay: !!indexData.absence_day,
    hasAnyRecords,
    canExportAny: indexData.can_export_any,
    workingDayStatus: indexData.working_day_status,
    optimisticTimeDelta,
    hasEditableRecords,
    isSticky: true,
  }

  return (
    <div>
      <DateNavigation
        date={currentDateObj}
        holiday={indexData.holiday}
        onDateChange={setDate}
        onPrev={goPrevDate}
        onNext={goNextDate}
      />

      <TotalRow {...totalRowProps} />

      {(shouldShowFilter || indexData.can_export_any) && (
        <div className="row mt-3 mb-3 align-items-start">
          <div className="col-md-8 col-lg-6 mb-2 mb-md-0">
            {shouldShowFilter && <RecordsFilter value={filterTerm} onChange={setFilterTerm} />}
          </div>
          <div className="col d-flex justify-content-md-end">
            {indexData.can_export_any && (
              <ExportToNavButton
                href={export_all_to_nav_resources_records_path({ date: currentDate })}
                confirmationMessage="Are you sure to export all records for the day to NAV?"
                label="Export all to NAV"
              />
            )}
          </div>
        </div>
      )}

      {indexData.from_approved_week && !indexData.can_create_entry && (
        <div className="alert alert-warning text-center">
          Dato er låst da timer er godkendt, kontakt løn eller IT hvis du har rettelser.
        </div>
      )}

      <RecordsList
        records={filteredRecords}
        absenceDay={filteredAbsenceDay}
        presenceDay={filteredPresenceDay}
        adjustments={filteredAdjustments}
      />

      <JobnumberSuggestions term={filterTerm} date={currentDate} enabled={shouldShowSuggestions} />

      {totalEntriesCount > 3 && (
        <div className="mt-4">
          <TotalRow {...totalRowProps} isSticky={false} />
        </div>
      )}
    </div>
  )
}

export default function ResourcesRecordsIndexApp(props: ResourcesRecordsIndexAppProps) {
  return (
    <QueryClientProvider client={queryClient}>
      <EditModeProvider>
        <TimeDeltaProvider>
          <ResourcesRecordsIndexContent {...props} />
        </TimeDeltaProvider>
      </EditModeProvider>
    </QueryClientProvider>
  )
}
